import React, { useRef } from "react";
import {
  caretAfterFormat,
  countDigitsBefore,
  digitsOnly,
  formatIrrGrouped,
  parseIrrInput,
  toEnglishDigits,
} from "../utils/irrPriceFormat";

/**
 * Isolated quote-admin unit price field with live thousands separators (ریال).
 * onValueChange receives a digit-only English string (no commas).
 */
export default function IrrPriceInput({
  value,
  onValueChange,
  id,
  disabled,
  style,
}) {
  const inputRef = useRef(null);
  const display = formatIrrGrouped(value == null ? "" : String(value));

  function commitRaw(rawText, digitsBeforeCaret) {
    const clean = digitsOnly(rawText);
    onValueChange(clean);
    const formatted = formatIrrGrouped(clean);
    requestAnimationFrame(() => {
      const node = inputRef.current;
      if (!node) return;
      const pos = caretAfterFormat(formatted, digitsBeforeCaret);
      try {
        node.setSelectionRange(pos, pos);
      } catch (_) {
        /* ignore */
      }
    });
  }

  function handleChange(e) {
    const el = e.target;
    const oldCaret =
      el.selectionStart == null ? el.value.length : el.selectionStart;
    const digitsBefore = countDigitsBefore(el.value, oldCaret);
    commitRaw(el.value, digitsBefore);
  }

  function handlePaste(e) {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData("text");
    const el = inputRef.current;
    const start = el?.selectionStart ?? display.length;
    const end = el?.selectionEnd ?? display.length;
    const before = display.slice(0, start);
    const after = display.slice(end);
    const merged = before + text + after;
    const digitsBefore = countDigitsBefore(
      before + toEnglishDigits(text),
      (before + toEnglishDigits(text)).length
    );
    commitRaw(merged, digitsBefore);
  }

  const parsed = parseIrrInput(display);
  const invalid = display !== "" && Number.isNaN(parsed);

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        flexWrap: "wrap",
      }}
    >
      <input
        ref={inputRef}
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        disabled={disabled}
        value={display}
        onChange={handleChange}
        onPaste={handlePaste}
        aria-label="فی واحد به ریال"
        style={{
          width: 168,
          direction: "ltr",
          textAlign: "left",
          fontSize: 16,
          fontVariantNumeric: "tabular-nums",
          border: invalid ? "1px solid #c8102e" : "1px solid #ced4da",
          borderRadius: 4,
          padding: "4px 8px",
          ...style,
        }}
      />
      <span style={{ fontSize: 15, fontWeight: 700, color: "#1f355e" }}>
        ریال
      </span>
    </span>
  );
}
